import { useTranslation } from 'react-i18next';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import validator from 'validator';
import { useDispatch } from 'react-redux';
import { NotificationType } from '../../../notification/types';
import { addNotification } from '../../../notification/slices/notificationSlice';
import { useGetAvatarQuery, useGetUserProductsQuery, useUpdateAvatarMutation, useUpdateProfileMutation } from '../../api/profileApi';
import { i18n } from '@senderra/translations';
import ContactInformationPreview from './contact-information-preview';
import Button from '../../../../components/button';
import FormInput from '../../../../components/forms/form-input';
import FormInputPhone from '../../../../components/forms/form-input-phone';
import Dialog from '../../../../components/dialog';
import ImageCropper from '../../../../components/image-cropper';
import SectionWrapper from '../shared/section-wrapper';
import FormWrapper from '../shared/form-wrapper';
import UserAvatar from '../../../../features/profile/components/shared/user-avatar';
import { User, UserFormValues } from '../../utils';
import { useUserRole } from '../../../user-management/hooks/useRoleProfile';
import { Product } from '../../../products/api/types';
import ContactInformationPreviewItem from './contact-information-preview/contact-information-preview-item';

interface ContactInformationProps {
    user: UserFormValues;
}

const schema = yup.object().shape({
    firstName: yup.string().required(i18n.t('first-name-required')),
    lastName: yup.string().required(i18n.t('last-name-required')),
    phoneNumber: yup
        .string()
        .test('is-valid-phone', i18n.t('invalid-phone-number'), (value) => {
            if (!value) return true;
            return /^\+1\d{10}$/.test(value);
        })
        .default(''),
    title: yup.string().default(''),
    email: yup
        .string()
        .required(i18n.t('email-required'))
        .test('is-valid-email', i18n.t('invalid-email'), (value) => (value ? validator.isEmail(value) : false)),
    avatarLink: yup.string().default(''),
});

const ContactInformation = ({ user }: ContactInformationProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [isPreview, setIsPreview] = useState(true);
    const [uploadDialog, setUploadDialog] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [updateProfile] = useUpdateProfileMutation();
    const [updateAvatar] = useUpdateAvatarMutation();

    const { permissions } = useUserRole();

    const showProducts = permissions.includes('PortalUser');

    const { data: products, isLoading: productsLoading } = useGetUserProductsQuery(undefined, { skip: !showProducts });
    const { data: userAvatar } = useGetAvatarQuery();


    const productsLabels = products ? products.map((product: Product) => product.shortName).join(', ') : '';

    const {
        register,
        handleSubmit,
        control,
        reset,
        formState: { errors },
    } = useForm<UserFormValues>({
        resolver: yupResolver(schema),
        defaultValues: user,
    });

    useEffect(() => {
        reset(user);
    }, [user, reset]);

    const onPhotoUpdate = async (image: FormData) => {
        try {
            await updateAvatar(image).unwrap();
            dispatch(
                addNotification({
                    message: t('profile-update-photo-success-notification'),
                    type: NotificationType.SUCCESS,
                })
            );
        } catch (error) {
            dispatch(
                addNotification({ message: t('profile-update-error-notification'), type: NotificationType.ERROR })
            );
        }
        setUploadDialog(false);
    };

    const onSubmit: SubmitHandler<UserFormValues> = async (data) => {
        try {
            if (!processing) {
                setProcessing(true);
                await updateProfile(data).unwrap();
            }
            dispatch(
                addNotification({
                    message: t('profile-update-success-notification'),
                    type: NotificationType.SUCCESS,
                })
            );
            setIsPreview(true);
        } catch (error) {
            dispatch(addNotification({ message: t('update-error-notification'), type: NotificationType.ERROR }));
        } finally {
            setProcessing(false);
        }
    };

    const togglePreview = () => {
        setIsPreview(!isPreview);
    };

    const handleCancel = () => {
        reset(user);
        togglePreview();
    };

    return (
        <SectionWrapper>
            <Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <UserAvatar firstName={user.firstName} lastName={user.lastName} avatarLink={userAvatar?.avatarDataUriContent} />
                    <Button
                        label={t('upload-photo')}
                        size='large'
                        variant='text'
                        sx={{ fontSize: '13px' }}
                        onClick={() => setUploadDialog(true)}
                    />
                </Box>
                {isPreview ? (
                    <ContactInformationPreview
                        user={user as User}
                        togglePreview={togglePreview}
                        showProducts={showProducts && !productsLoading}
                        products={productsLabels}
                    />
                ) : (
                    <Box component='form' onSubmit={handleSubmit(onSubmit)} sx={{ marginTop: '24px' }}>
                        <FormWrapper>
                            <FormInput
                                name='firstName'
                                label={`${t('first-name')}*`}
                                register={register}
                                error={!!errors.firstName}
                                helperText={errors.firstName?.message}
                            />
                            <FormInput
                                name='lastName'
                                label={`${t('last-name')}*`}
                                register={register}
                                error={!!errors.lastName}
                                helperText={errors.lastName?.message}
                            />
                            <FormInput
                                name='title'
                                label={t('title')}
                                register={register}
                                error={!!errors.title}
                                helperText={errors.title?.message}
                            />
                            <FormInput
                                name='email'
                                label={`${t('email')}*`}
                                register={register}
                                error={!!errors.email}
                                helperText={errors.email?.message}
                                isDisabled
                            />
                            <FormInputPhone
                                name='phoneNumber'
                                control={control}
                                label={t('phone-number')}
                                error={!!errors.phoneNumber}
                                helperText={errors.phoneNumber?.message}
                            />
                            {showProducts && (
                                <ContactInformationPreviewItem label={t('assigned-products')} title={productsLabels} />
                            )}
                        </FormWrapper>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                width: '100%',
                                marginTop: '24px',
                                gap: '16px',
                            }}
                        >
                            <Button label={t('cancel')} size='large' variant='outlined' onClick={handleCancel} />
                            <Button label={t('save')} size='large' variant='primary' type='submit' />
                        </Box>
                    </Box>
                )}
            </Box>
            <Dialog isOpen={uploadDialog} title={t('upload-photo')} onClose={() => setUploadDialog(false)}>
                <ImageCropper
                    mainActionTitle={t('upload')}
                    secondaryActionTitle={t('cancel')}
                    onSave={onPhotoUpdate}
                    onCancel={() => setUploadDialog(false)}
                />
            </Dialog>
        </SectionWrapper>
    );
};

export default ContactInformation;
